import { Fragment, useCallback, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useUserStore } from '@Auth/store';
import { Show } from '@components/Show';
import type { UIComponentBase } from '@components/types';
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip';
import { useIsCommandPaletteEnabled } from '@features/command-palette';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { MenuIcon, XIcon } from '@heroicons/react/outline';
import { useDeepCompareMemo } from '@hooks/use-deep-compare-memo';
import { useDocumentTitle } from '@hooks/useDocumentTitle';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { ModalContextProvider, ModalSwitch } from '@hooks/useModalProvider';
import { useStickyState } from '@hooks/useStickyState';
import type { TenantName } from '@root/config/types';
import { MenuSectionUser } from '@User/Menu/MenuSection.User';
import SupportButton from '@User/Menu/SupportButton';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { tenantConfig } from '../../config/tenants';
import Nav from '../routes';

import { layoutChildrenClasses } from './classes';
import { LogoCollaboration } from './LogoCollaboration';
import { createMenuForUser } from './menu';
import { MenuNavItems } from './MenuNavItems';
import { NavSearchField } from './NavSearchField';

const client: TenantName = import.meta.env?.VITE_CLIENT ?? process?.env?.CLIENT;

const SIDEBAR_WIDTH = { collapsed: 60, expanded: 270 } as const;
const SIDEBAR_TRANSITION = { duration: 0.3, ease: 'easeInOut' } as const;

const MenuLayout = ({ children }: UIComponentBase) => {
  const getFeatureFlagState = useFeatureFlags();
  const isCommandPaletteEnabled = useIsCommandPaletteEnabled();
  // Limited preview: the eval-configs page is allowlist-gated server-side,
  // so hide its menu entry from callers without feature_previews access.
  const { pbqaEval: hasPbqaAccess } = useFeatureAccess();
  useDocumentTitle();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useStickyState(false, 'sidebar-collapsed');
  const { user } = useUserStore(({ user }) => ({ user }));
  const location = useLocation();
  const currentHref = location.pathname;
  const headerLogoProps = {
    height: tenantConfig.headerLogoHeight,
    width: tenantConfig.headerLogoWidth,
  };

  const navigation = useDeepCompareMemo(
    () => createMenuForUser(user, getFeatureFlagState).filter((item) => item.path !== Nav.EvalConfigs || hasPbqaAccess),
    [user, getFeatureFlagState, hasPbqaAccess],
  );

  const isActive = useCallback(
    (path: Nav | string) => {
      if (currentHref.startsWith(path) && !path.includes(':')) {
        return true;
      }

      const match = matchPath({ path, end: false }, currentHref);
      return !!match;
    },
    [currentHref],
  );

  const removePadding = useMemo(() => {
    if (isActive(Nav.Block) || isActive(Nav.Clause)) {
      return true;
    }

    if (getFeatureFlagState('Branding')) {
      return isActive(Nav.BrandingDetails);
    }
    return false;
    // eslint-disable-next-line -- observing the currentHref is enough
  }, [currentHref]);

  const enableContainerOverflow = useMemo(() => {
    return isActive(Nav.PreDraftContract);
  }, [isActive]);

  const expandSidebar = () => setIsSidebarCollapsed(false);

  const handleSidebarToggleClick = () => setIsSidebarCollapsed((current) => !current);

  const PanelLeftComponent = isSidebarCollapsed ? PanelLeftOpen : PanelLeftClose;

  return (
    <ModalContextProvider>
      <div className="admin h-full">
        <ModalSwitch />
        <Transition show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="relative z-40 md:hidden" onClose={setSidebarOpen}>
            <TransitionChild
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="bg-info-600/75 fixed inset-0" />
            </TransitionChild>

            <div className="fixed inset-0 z-40 flex">
              <TransitionChild
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <DialogPanel className="bg-info-800 relative flex w-full max-w-xs flex-1 flex-col">
                  <TransitionChild
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute top-0 right-0 -mr-12 pt-2">
                      <button
                        type="button"
                        className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:ring-2 focus:ring-white focus:outline-hidden focus:ring-inset"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                      </button>
                    </div>
                  </TransitionChild>
                  <div className="h-0 flex-1 overflow-y-auto pt-5 pb-4">
                    {client === 'ct-ppl' ? (
                      <div className="mx-auto my-3 px-4">
                        <LogoCollaboration logoColor="white" size="sm" />
                      </div>
                    ) : (
                      <div className="flex shrink-0 items-center px-4">
                        <img
                          className={`w-auto ${tenantConfig.headerLogoClasses}`}
                          src={tenantConfig.headerLogo}
                          alt="Logo"
                        />
                      </div>
                    )}
                    <nav className="mt-5 space-y-1 px-2">
                      <MenuNavItems navigation={navigation} isActive={isActive} variant="mobile" isCollapsed={false} />
                    </nav>
                  </div>
                  <Show when={!!user}>
                    <MenuSectionUser user={user!} displayAvatar isCollapsible />
                  </Show>
                </DialogPanel>
              </TransitionChild>
              <div className="w-14 shrink-0">{/* Force sidebar to shrink to fit close icon */}</div>
            </div>
          </Dialog>
        </Transition>

        {/* Static sidebar for desktop */}
        <motion.div
          className="hidden overflow-x-hidden border-r md:fixed md:inset-y-0 md:flex md:flex-col"
          initial={false}
          animate={{ width: isSidebarCollapsed ? SIDEBAR_WIDTH.collapsed : SIDEBAR_WIDTH.expanded }}
          transition={SIDEBAR_TRANSITION}
        >
          <div className="bg-info-800 flex min-h-0 flex-1 flex-col overflow-x-hidden">
            <div className="flex flex-1 flex-col overflow-x-hidden overflow-y-auto pt-5 pb-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={isSidebarCollapsed ? 'Open Panel' : 'Close Panel'}
                    className="text-info-400 hover:text-info-300 absolute top-0 right-0 mb-1 inline-flex items-center rounded-md p-2"
                    onClick={() => handleSidebarToggleClick()}
                  >
                    <PanelLeftComponent size={18} strokeWidth={1.5} aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-info-900 rounded-[5px] px-[11px] py-2 text-xs text-white">
                  {isSidebarCollapsed ? 'Open Panel' : 'Close Panel'}
                </TooltipContent>
              </Tooltip>
              {client === 'ct-ppl' ? (
                <div className="mx-auto my-3 px-4">
                  <LogoCollaboration logoColor="white" size="sm" isCollapsed={isSidebarCollapsed} />
                </div>
              ) : (
                <div className="mx-auto mt-4 flex h-12 shrink-0 items-center justify-center">
                  <AnimatePresence initial={false}>
                    {isSidebarCollapsed ? (
                      <img
                        src="/assets/Artificial_Sygnet_White_New.svg"
                        alt="Logo"
                        width={24}
                        height={18}
                        className="h-[18px] w-6"
                      />
                    ) : (
                      <motion.img
                        className={`w-auto max-w-none ${tenantConfig.headerLogoClasses}`}
                        src={tenantConfig.headerLogo}
                        alt="Logo"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={SIDEBAR_TRANSITION}
                        {...headerLogoProps}
                      />
                    )}
                  </AnimatePresence>
                </div>
              )}

              {isCommandPaletteEnabled && <NavSearchField isCollapsed={isSidebarCollapsed} />}

              <nav className="mt-5 flex-1 space-y-1 px-2">
                <MenuNavItems
                  navigation={navigation}
                  isActive={isActive}
                  variant="desktop"
                  isCollapsed={isSidebarCollapsed}
                />
                <SupportButton isCollapsed={isSidebarCollapsed} />
              </nav>
            </div>
            <Show when={!!user}>
              <MenuSectionUser
                user={user!}
                displayAvatar
                isCollapsible
                isCollapsed={isSidebarCollapsed}
                onExpandRequest={expandSidebar}
              />
            </Show>
          </div>
        </motion.div>
        <motion.div
          className="flex h-full flex-1 flex-col max-md:!pl-0"
          initial={false}
          animate={{
            paddingLeft: isSidebarCollapsed ? SIDEBAR_WIDTH.collapsed : SIDEBAR_WIDTH.expanded,
          }}
          transition={SIDEBAR_TRANSITION}
        >
          <div className="bg-info-100 sticky top-0 z-10 pt-1 pl-1 sm:pt-3 sm:pl-3 md:hidden">
            <button
              type="button"
              className="text-info-500 hover:text-info-900 focus:ring-primary-500 -mt-0.5 -ml-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md focus:ring-2 focus:outline-hidden focus:ring-inset"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <MenuIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <main className="h-full flex-1">
            <div
              className={layoutChildrenClasses({
                padding: !removePadding,
                overflow: enableContainerOverflow,
              })}
            >
              {children}
            </div>
          </main>
        </motion.div>
      </div>
    </ModalContextProvider>
  );
};

export default MenuLayout;
